import React, { useEffect, useState, useRef, useCallback } from 'react';

const CustomCursor = () => {
  const [isHovering, setIsHovering] = useState(false);
  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const followerPos = useRef({ x: 0, y: 0 });
  const trailsRef = useRef([]);
  const trailContainerRef = useRef(null);
  const lastTrailTime = useRef(0);
  const hoverTimeoutRef = useRef(null);
  const isHoveringRef = useRef(false);

  const addTrail = useCallback((x, y) => {
    if (!trailContainerRef.current) return;
    
    // Limit trail count for performance
    if (trailsRef.current.length > 15) {
      const oldTrail = trailsRef.current.shift();
      if (oldTrail && oldTrail.parentNode) {
        oldTrail.parentNode.removeChild(oldTrail);
      }
    }
    
    const trail = document.createElement('div');
    trail.className = 'mouse-trail';
    trail.style.left = x + 'px';
    trail.style.top = y + 'px';
    
    trailContainerRef.current.appendChild(trail);
    trailsRef.current.push(trail);
    
    // Remove trail after animation
    setTimeout(() => {
      if (trail.parentNode) {
        trail.parentNode.removeChild(trail);
        const index = trailsRef.current.indexOf(trail);
        if (index > -1) {
          trailsRef.current.splice(index, 1);
        }
      }
    }, 800);
  }, []);

  const updateCursorPosition = useCallback((e) => {
    mousePos.current = { x: e.clientX, y: e.clientY };
    
    // Update main cursor immediately with left/top and let CSS transform center it
    if (cursorRef.current) {
      cursorRef.current.style.left = e.clientX + 'px';
      cursorRef.current.style.top = e.clientY + 'px';
    }
    
    // Add trail with throttling to reduce lag
    const now = Date.now();
    if (now - lastTrailTime.current > 16) { // ~60fps throttling
      addTrail(e.clientX, e.clientY);
      lastTrailTime.current = now;
    }
  }, [addTrail]);

  const animateFollower = useCallback(() => {
    if (!followerRef.current) return;
    
    const dx = mousePos.current.x - followerPos.current.x;
    const dy = mousePos.current.y - followerPos.current.y;
    
    followerPos.current.x += dx * 0.1;
    followerPos.current.y += dy * 0.1;
    
    followerRef.current.style.left = followerPos.current.x + 'px';
    followerRef.current.style.top = followerPos.current.y + 'px';
    
    requestAnimationFrame(animateFollower);
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
    if (!isHoveringRef.current) {
      isHoveringRef.current = true;
      setIsHovering(true);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      isHoveringRef.current = false;
      setIsHovering(false);
    }, 50); // Small delay to prevent flickering
  }, []);

  useEffect(() => {

    // Add event listeners to interactive elements
    const interactiveElements = document.querySelectorAll(
      'a, button, .btn, .action-btn, .project-link, .skill-orb, .glass-card, .project-card'
    );

    // Initialize follower position
    followerPos.current = { x: 0, y: 0 };

    document.addEventListener('mousemove', updateCursorPosition, { passive: true });
    
    interactiveElements.forEach(el => {
      el.addEventListener('mouseenter', handleMouseEnter, { passive: true });
      el.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    });

    // Start follower animation
    const animationId = requestAnimationFrame(animateFollower);

    return () => {
      document.removeEventListener('mousemove', updateCursorPosition);
      interactiveElements.forEach(el => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
      cancelAnimationFrame(animationId);
      clearTimeout(hoverTimeoutRef.current);
      
      // Cleanup trails
      trailsRef.current.forEach(trail => {
        if (trail.parentNode) {
          trail.parentNode.removeChild(trail);
        }
      });
      trailsRef.current = [];
    };
  }, [updateCursorPosition, animateFollower, handleMouseEnter, handleMouseLeave]);

  return (
    <>
      {/* Main Cursor */}
      <div
        ref={cursorRef}
        className={`custom-cursor ${isHovering ? 'hover' : ''}`}
        style={{ left: 0, top: 0 }}
      />
      
      {/* Cursor Follower */}
      <div
        ref={followerRef}
        className={`custom-cursor-follower ${isHovering ? 'hover' : ''}`}
        style={{ left: 0, top: 0 }}
      />
      
      {/* Mouse Trails Container */}
      <div ref={trailContainerRef} className="cursor-trail-container" />
    </>
  );
};

export default CustomCursor;